import { ofetch, type $Fetch } from 'ofetch';
import { DomainError } from '@/domain/error/DomainError';

type TokenProvider = () => string | null;
type OnUnauthorized = () => Promise<string | null>; // tenta refresh; retorna novo access ou null

interface ClientConfig {
  baseURL: string;
  getAccessToken: TokenProvider;
  onUnauthorized: OnUnauthorized;
}

/**
 * Cliente ofetch tipado com:
 *  - Authorization Bearer injetado quando há access token
 *  - 401 → tenta 1 refresh (via callback do AuthService) e retry
 *  - Resposta de erro convertida em DomainError (RFC 7807 do backend)
 *
 * Retry de 401 acontece dentro do retryStatusCodes do próprio ofetch — a função
 * onRequest pega o token atualizado na 2ª chamada.
 */
export function createHttpClient(config: ClientConfig): $Fetch {
  return ofetch.create({
    baseURL: config.baseURL,
    retry: 1,
    retryStatusCodes: [401],
    async onRequest({ options }) {
      // Em retry de 401, dispara refresh ANTES da segunda tentativa.
      const headers = new Headers(options.headers as HeadersInit | undefined);
      let token = config.getAccessToken();
      const isRetry = headers.has('X-AVA-Retried');
      if (isRetry) {
        token = await config.onUnauthorized();
      }
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('X-AVA-Retried', '1');
      options.headers = headers;
    },
    onResponseError({ response }) {
      throw DomainError.fromApiError(response._data, response.status);
    },
  });
}
